import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ProfileInfo from './profileInfo';
import PasswordSecurity from './PasswordSecurity'
import AcademicInfo from './AcademicInfo'

const ProfileSettings = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('profile');
    const [isLoading, setIsLoading] = useState(true);
    const [avatarPreview, setAvatarPreview] = useState('');
    const [fileError, setFileError] = useState('');
    const [formErrors, setFormErrors] = useState({});
    const [passwordErrors, setPasswordErrors] = useState({});

    const [studentData, setStudentData] = useState({
        avatar: 'https://randomuser.me/api/portraits/men/1.jpg',
        name: 'John Doe',
        email: 'john.doe@university.edu',
        phone: '9585133254',
        dob: '2000-05-15',
        gender: 'male',
        address: '123 Campus Ave, University Town',
        bio: 'Computer Science major with focus on AI and Machine Learning',
        studentId: 'STU2023001',
        department: 'Computer Science',
        semester: '4',
        enrollmentDate: '2021-09-01',
        courses: ['CS101', 'CS202', 'MATH301', 'PHYS102']
    });

    const [formData, setFormData] = useState({ ...studentData });
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });


    useEffect(() => {
        const timer = setTimeout(() => {
            setIsLoading(false);
        }, 800);

        return () => clearTimeout(timer);
    }, []);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setFileError('');

        if (!file) return;

        if (!file.type.match('image.*')) {
            setFileError('Please select an image file (JPEG, PNG, GIF)');
            return;
        }

        if (file.size > 2 * 1024 * 1024) {
            setFileError('File size must be less than 2MB');
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
            setAvatarPreview(reader.result);
        };
        reader.readAsDataURL(file);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordData(prev => ({ ...prev, [name]: value }));
        if (passwordErrors[name]) {
            setPasswordErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateProfileForm = () => {
        const errors = {};
        if (!formData.name.trim()) errors.name = 'Name is required';
        if (!formData.phone.trim()) errors.phone = 'Phone is required';
        if (!formData.dob) errors.dob = 'Date of birth is required';
        if (!formData.address.trim()) errors.address = 'Address is required';

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const validatePasswordForm = () => {
        const errors = {};
        if (!passwordData.currentPassword) errors.currentPassword = 'Current password is required';
        if (!passwordData.newPassword) {
            errors.newPassword = 'New password is required';
        } else if (passwordData.newPassword.length < 8) {
            errors.newPassword = 'Password must be at least 8 characters';
        }
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            errors.confirmPassword = 'Passwords do not match';
        }

        setPasswordErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateProfileForm()) return;

        setIsLoading(true);
        try {
            await new Promise(resolve => setTimeout(resolve, 1000));
            setStudentData({ ...formData, avatar: avatarPreview || studentData.avatar });
            alert('Profile updated successfully!');
        } catch (error) {
            alert('Failed to update profile. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        if (!validatePasswordForm()) return;

        setIsLoading(true);
        try {
            await new Promise(resolve => setTimeout(resolve, 1000));
            setPasswordData({
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            });
            alert('Password changed successfully!');
        } catch (error) {
            alert('Failed to change password. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
            </div>
        );
    }

    return (
        <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex flex-col md:flex-row gap-8">
                <div className="md:w-1/4">
                    <div className="bg-white rounded-xl shadow-md p-6 sticky top-8">
                        <div className="flex flex-col items-center mb-6">
                            <div className="relative group mb-4">
                                <div className="w-24 h-24 rounded-full bg-gray-200 overflow-hidden border-4 border-white shadow-md">
                                    <img
                                        src={avatarPreview || studentData.avatar}
                                        alt="Profile"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <label className="absolute bottom-0 right-0 bg-teal-600 text-white p-2 rounded-full cursor-pointer hover:bg-teal-700 transition">
                                    <input
                                        type="file"
                                        className="hidden"
                                        onChange={handleFileChange}
                                        accept="image/*"
                                    />
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                    </svg>
                                </label>
                            </div>
                            <h2 className="text-xl font-bold text-gray-800">{studentData.name}</h2>
                            <p className="text-sm text-gray-500">{studentData.studentId}</p>
                            {fileError && <p className="mt-2 text-xs text-red-500">{fileError}</p>}
                        </div>

                        <nav className="space-y-2">
                            <button
                                onClick={() => setActiveTab('profile')}
                                className={`w-full text-left px-4 py-3 rounded-lg flex items-center transition ${activeTab === 'profile' ? 'bg-teal-50 text-teal-600 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                </svg>
                                Profile Information
                            </button>
                            <button
                                onClick={() => setActiveTab('password')}
                                className={`w-full text-left px-4 py-3 rounded-lg flex items-center transition ${activeTab === 'password' ? 'bg-teal-50 text-teal-600 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                </svg>
                                Password & Security
                            </button>
                            <button
                                onClick={() => setActiveTab('academic')}
                                className={`w-full text-left px-4 py-3 rounded-lg flex items-center transition ${activeTab === 'academic' ? 'bg-teal-50 text-teal-600 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" viewBox="0 0 20 20" fill="currentColor">
                                    <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
                                </svg>
                                Academic Information
                            </button>
                        </nav>
                    </div>
                </div>

                <div className="md:w-3/4">
                    <div className="bg-white rounded-xl shadow-md overflow-hidden">
                        {/* Profile Information */}
                        {activeTab === 'profile' && (
                            <ProfileInfo handleSubmit={handleSubmit} formData={formData} handleChange={handleChange} formErrors={formErrors} navigate={navigate} isLoading={isLoading} />
                        )}

                        {/* Password & Security */}
                        {activeTab === 'password' && (
                            <PasswordSecurity passwordErrors={passwordErrors} handlePasswordSubmit={handlePasswordSubmit} passwordData={passwordData} handlePasswordChange={handlePasswordChange} isLoading={isLoading}/>
                        )}

                        {/* Academic Information */}
                        {activeTab === 'academic' && (
                            <AcademicInfo  studentData={studentData}/>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProfileSettings;